import { ComparisonStatus, ShiftKpiType, VolumeTargetType, MetricName, ProcessPathType } from './constants';
import {
    ShiftTimeContext,
    VolumeTargetComparisonData,
    HourlyShiftComparisonData,
    MonitoringEntityIdentityComparisonData,
    MetricComparisonData,
} from './model';

/**
 * Formats a number to exactly 2 decimal places.
 * Used for consistent display in comparison tables.
 */
export const formatMetricValue = (value: number): string => {
    return value.toFixed(2);
};

/**
 * Formats a number as a whole number (no decimals). Used by the V2 Progress
 * Report table, where every metric value is a whole number.
 */
export const formatWholeMetricValue = (value: number): string => {
    return Math.round(value).toString();
};

const wholeNumberFormatter = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });

/**
 * Formats a volume as a whole number with thousands separators (e.g. 23236.4 → "23,236").
 * Volumes (ALPS / plan / actual units) are always shown as whole numbers, never fractions.
 */
export const formatWholeNumber = (value: number): string => wholeNumberFormatter.format(Math.round(value));

/**
 * Formats an absolute instant (epoch ms) as a human-readable local date + time,
 * e.g. "Sep 7, 2026, 1:05 PM". Used for the dashboard "last updated" stamp that
 * reflects when the backend response was received.
 */
export const formatTimestamp = (epochMs: number): string =>
    new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    }).format(new Date(epochMs));

/**
 * Formats an "Actual | Expected" cell string with uniform width.
 * Each number: up to 5 digits before decimal, exactly 2 after.
 * Uses padStart for alignment so the '|' separator stays vertically aligned.
 */
export const formatComparisonCell = (actual: number, expected: number): string => {
    const maxWidth = 8; // 5 digits + '.' + 2 decimals = 8 chars
    const actualStr = actual.toFixed(2).padStart(maxWidth);
    const expectedStr = expected.toFixed(2).padStart(maxWidth);
    return `${actualStr} | ${expectedStr}`;
};

/**
 * Maps ComparisonStatus to display color.
 * Colors align with Meridian semantic tokens:
 * - CRITICAL → error/red
 * - AT_RISK → warning/orange-amber
 * - ON_TRACK → informational/blue
 * - AHEAD → success/green
 */
export const STATUS_COLORS: Record<string, string> = {
    [ComparisonStatus.CRITICAL]: '#d13212',
    [ComparisonStatus.AT_RISK]: '#eb5f07',
    [ComparisonStatus.ON_TRACK]: '#0073bb',
    [ComparisonStatus.AHEAD]: '#1d8102',
};

/**
 * Returns the color for a given ComparisonStatus.
 * Falls back to default text color if status is unknown.
 */
export const getStatusColor = (status: string | undefined): string => {
    if (!status) return 'inherit';
    return STATUS_COLORS[status] || 'inherit';
};

/**
 * Maps a ComparisonStatus to the Meridian Tag `type` used for the delta badge:
 * - CRITICAL → error (red)
 * - AT_RISK  → warning (amber)
 * - AHEAD / ON_TRACK → success (green)
 * Unknown/undefined statuses fall back to a neutral tag.
 */
export const getStatusTagType = (
    status: string | undefined
): 'success' | 'warning' | 'error' | 'neutral' => {
    switch (status) {
        case ComparisonStatus.CRITICAL:
            return 'error';
        case ComparisonStatus.AT_RISK:
            return 'warning';
        case ComparisonStatus.AHEAD:
        case ComparisonStatus.ON_TRACK:
            return 'success';
        default:
            return 'neutral';
    }
};

/**
 * Formats a variance percentage into a signed, whole-number badge label
 * (e.g. 2.4 → "+2%", -6.1 → "-6%"). Matches the delta pills in the design.
 */
export const formatDeltaPercent = (variancePercentage: number | undefined): string => {
    if (variancePercentage == null || Number.isNaN(variancePercentage)) return '';
    const rounded = Math.round(variancePercentage);
    return `${rounded > 0 ? '+' : ''}${rounded}%`;
};

/**
 * Top-accent-bar color for the Maestro summary cards, driven by ComparisonStatus:
 * - AHEAD / ON_TRACK → green
 * - AT_RISK → amber
 * - CRITICAL → red
 * Unknown/undefined falls back to a neutral grey.
 */
export const STATUS_ACCENT_COLORS: Record<string, string> = {
    [ComparisonStatus.AHEAD]: '#1d8102',
    [ComparisonStatus.ON_TRACK]: '#1d8102',
    [ComparisonStatus.AT_RISK]: '#eb5f07',
    [ComparisonStatus.CRITICAL]: '#d13212',
};

export const getStatusAccentColor = (status: string | undefined): string => {
    if (!status) return '#8d9096';
    return STATUS_ACCENT_COLORS[status] || '#8d9096';
};

/**
 * Derives a ComparisonStatus from a signed variance percentage (how far a value
 * is from its target, e.g. `(projected / target - 1) * 100`). Mirrors the
 * backend's default thresholds (see RnRRCCFacadeServiceModel ComparisonStatus):
 * - AHEAD:    > +5% above target
 * - ON_TRACK: within ±5% of target
 * - AT_RISK:  5–15% below target
 * - CRITICAL: > 15% below target
 *
 * Used for client-side projections (e.g. Projected EOS) that have no
 * server-computed status. Thresholds may become site-configurable server-side;
 * keep this in sync when they do.
 */
export const getComparisonStatusFromVariance = (variancePercentage: number): ComparisonStatus => {
    if (variancePercentage > 5) return ComparisonStatus.AHEAD;
    if (variancePercentage >= -5) return ComparisonStatus.ON_TRACK;
    if (variancePercentage >= -15) return ComparisonStatus.AT_RISK;
    return ComparisonStatus.CRITICAL;
};

/**
 * Soft, opaque background tints for the shift-progress banner, keyed by
 * ComparisonStatus. These are intentionally light (not the strong
 * STATUS_ACCENT_COLORS) so the banner reads as a subtle status wash behind
 * dark-status text — matching the design:
 * - AHEAD / ON_TRACK → pale green
 * - AT_RISK → pale amber
 * - CRITICAL → pale red
 * Unknown/undefined falls back to a neutral grey wash.
 */
export const STATUS_BANNER_BACKGROUND_COLORS: Record<string, string> = {
    [ComparisonStatus.AHEAD]: '#eff8ef',
    [ComparisonStatus.ON_TRACK]: '#eff8ef',
    [ComparisonStatus.AT_RISK]: '#fdf3ec',
    [ComparisonStatus.CRITICAL]: '#fdecea',
};

export const getStatusBannerBackgroundColor = (status: string | undefined): string => {
    if (!status) return '#f2f3f3';
    return STATUS_BANNER_BACKGROUND_COLORS[status] || '#f2f3f3';
};

/**
 * Projected end-of-shift volume via linear progression.
 *
 * While the shift is in progress we assume the current pace holds for the rest
 * of the shift, so the projection is `doneSoFar` scaled up by the fraction of
 * the shift that has elapsed:
 *
 *     projected = doneSoFar / elapsedFraction
 *
 * where `elapsedFraction = (now - shiftStart) / (shiftEnd - shiftStart)`.
 *
 * Edge cases:
 * - Shift already over (now >= shiftEnd): no projection needed — return the
 *   actual `doneSoFar`.
 * - Shift not yet started (now <= shiftStart): nothing has elapsed to project
 *   from — return the actual `doneSoFar` (typically 0).
 * - Missing/invalid shift window: return null so the card renders an em dash.
 *
 * The result is rounded to the nearest whole unit; formatting (thousands
 * separators) is left to the caller/UI.
 */
export const computeProjectedEos = (
    doneSoFar: number,
    context: ShiftTimeContext | undefined,
    nowMs: number
): number | null => {
    if (!context?.shiftStartTime || !context?.shiftEndTime) return null;

    const startMs = new Date(context.shiftStartTime).getTime();
    const endMs = new Date(context.shiftEndTime).getTime();
    if (Number.isNaN(startMs) || Number.isNaN(endMs) || endMs <= startMs) return null;

    const elapsedFraction = (nowMs - startMs) / (endMs - startMs);

    // Outside the shift window (before start or after end) there is no live pace
    // to extrapolate from, so the actual processed volume is the answer.
    if (elapsedFraction <= 0 || elapsedFraction >= 1) return doneSoFar;

    return Math.round(doneSoFar / elapsedFraction);
};

const projectedRangeFormatter = new Intl.NumberFormat('en-US');

/**
 * A ±5% confidence band around the projected EOS value, formatted as
 * "low – high" (e.g. "89,300 – 98,700"). Both bounds are rounded to whole
 * units. Returns null when there is no projection, so the card shows an em dash.
 */
export const computeProjectedEosRange = (projectedEos: number | null | undefined): string | null => {
    if (projectedEos == null) return null;
    const low = Math.round(projectedEos * 0.95);
    const high = Math.round(projectedEos * 1.05);
    return `${projectedRangeFormatter.format(low)} – ${projectedRangeFormatter.format(high)}`;
};

/**
 * The single source of truth for the volume projection shown in BOTH the pace
 * banner and the Maestro VOLUME card. Derived from the ALPS volume-target
 * comparison plus the shift window, so the two surfaces can never drift.
 */
export interface VolumeProjectionSummary {
    /** ALPS actual volume processed so far. */
    actualValue: number | null;
    /** ALPS plan/target volume. */
    targetValue: number | null;
    /** Server-computed actual-vs-plan status (drives banner coloring). */
    comparisonStatus?: ComparisonStatus;
    /** Linear-pace projection for end of shift. */
    projectedEos: number | null;
    /** projectedEos / target, as a whole percentage. */
    projectedPercent: number | null;
    /** ±5% band around projectedEos, formatted "low – high". */
    projectedRange: string | null;
    /** Projected units above (+) or below (−) plan at end of shift. */
    paceDelta: number | null;
}

/**
 * Builds the VolumeProjectionSummary from the compareFlowMonitoringData volume
 * targets and shift window at a given instant. Reads the ALPS target; all other
 * fields fall back to null when ALPS data or the shift window is missing.
 */
export const deriveVolumeProjectionSummary = (
    volumeTargetComparisons: VolumeTargetComparisonData[] | undefined,
    shiftTimeContext: ShiftTimeContext | undefined,
    nowMs: number
): VolumeProjectionSummary => {
    const alps = volumeTargetComparisons?.find((v) => v.targetType === VolumeTargetType.ALPS_TARGET);
    const actualValue = alps?.actualValue ?? null;
    const targetValue = alps?.targetValue ?? null;

    const projectedEos = actualValue == null ? null : computeProjectedEos(actualValue, shiftTimeContext, nowMs);
    const hasPlan = projectedEos != null && targetValue != null && targetValue !== 0;

    return {
        actualValue,
        targetValue,
        comparisonStatus: alps?.comparisonStatus,
        projectedEos,
        projectedPercent: hasPlan ? Math.round((projectedEos! / targetValue!) * 100) : null,
        projectedRange: computeProjectedEosRange(projectedEos),
        paceDelta: projectedEos != null && targetValue != null ? projectedEos - targetValue : null,
    };
};

/** Projected end-of-shift value for one KPI card (+ % of target and range). */
export interface KpiProjection {
    /** Projected EOS value; null renders an em dash. */
    projectedEos: number | null;
    /** projectedEos / target, as a whole percentage; null when no target. */
    projectedPercent: number | null;
    /** ±5% band around projectedEos, "low – high"; null when no projection. */
    projectedRange: string | null;
}

/**
 * Projects a KPI's end-of-shift value, reusing the volume machinery but
 * respecting each metric's kind:
 * - VOLUME_COMPLETED — cumulative, so extrapolate by elapsed shift fraction
 *   (`computeProjectedEos`): a partial total scales up to the full shift.
 * - TPH / HC_STAFFED — a rate / point-in-time value, so the EOS projection is
 *   "current holds" (projected = actual); dividing by elapsed fraction would be
 *   nonsensical (a 29%-elapsed rate is not 1/0.29 of itself).
 * - SUPPORT_RATE — no projection in the mock; returns null (em dash).
 *
 * `actual`/`target` are the card's already-resolved values. The ±5% range is
 * shared with the volume card via `computeProjectedEosRange`.
 */
export const deriveKpiProjection = (
    kpiType: ShiftKpiType,
    actual: number | null | undefined,
    target: number | null | undefined,
    shiftTimeContext: ShiftTimeContext | undefined,
    nowMs: number
): KpiProjection => {
    let projectedEos: number | null = null;
    if (actual != null) {
        if (kpiType === ShiftKpiType.VOLUME_COMPLETED) {
            projectedEos = computeProjectedEos(actual, shiftTimeContext, nowMs);
        } else if (kpiType === ShiftKpiType.TPH || kpiType === ShiftKpiType.HC_STAFFED) {
            projectedEos = actual; // rate / point-in-time: current pace holds to EOS
        }
        // SUPPORT_RATE intentionally has no projection (em dash).
    }

    const hasPercent = projectedEos != null && target != null && target !== 0;
    return {
        projectedEos,
        projectedPercent: hasPercent ? Math.round((projectedEos! / target!) * 100) : null,
        projectedRange: computeProjectedEosRange(projectedEos),
    };
};

// ─── Shift Timeline ────────────────────────────────────────────────

const HOUR_MS = 60 * 60 * 1000;

/**
 * Formats an absolute instant (epoch ms) as "HH:mm" in the given IANA timezone.
 * Falls back to the viewer's local zone when timezoneId is missing.
 */
export const formatTimeInZone = (epochMs: number, timezoneId?: string): string =>
    new Intl.DateTimeFormat('en-US', {
        timeZone: timezoneId || undefined,
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    }).format(new Date(epochMs));

/** A single hour tick on the shift timeline axis. */
export interface ShiftTimelineTick {
    /** Warehouse-local "HH:mm" label. */
    label: string;
    /** Horizontal position along the axis, 0–100 (%). */
    positionPercent: number;
}

/** Everything the timeline component needs to render, derived from ShiftTimeContext. */
export interface ShiftTimelineModel {
    ticks: ShiftTimelineTick[];
    /** Filled fraction of the axis (0–100), clamped to the shift window. */
    progressPercent: number;
    /** "NOW" marker position (0–100), or null when now is outside the shift window. */
    nowPositionPercent: number | null;
    /** Warehouse-local "HH:mm" label for now, or null when outside the window. */
    nowLabel: string | null;
}

/**
 * Builds the timeline view model from a ShiftTimeContext and an absolute
 * "current instant" (epoch ms — callers pass the skew-corrected device clock).
 *
 * Positions are computed purely from epoch instants, so they are independent of
 * the viewer's timezone; only the labels are rendered in `timezoneId`. Hour
 * ticks are stepped hourly from the start, which lands on whole warehouse-local
 * hours whenever the shift starts on the hour. Returns null when the window is
 * missing or non-positive.
 */
export const computeShiftTimelineModel = (
    context: ShiftTimeContext | undefined,
    nowMs: number
): ShiftTimelineModel | null => {
    if (!context?.shiftStartTime || !context?.shiftEndTime) return null;

    const startMs = new Date(context.shiftStartTime).getTime();
    const endMs = new Date(context.shiftEndTime).getTime();
    if (Number.isNaN(startMs) || Number.isNaN(endMs) || endMs <= startMs) return null;

    const span = endMs - startMs;
    const tz = context.timezoneId;

    const ticks: ShiftTimelineTick[] = [];
    for (let t = startMs; t < endMs; t += HOUR_MS) {
        ticks.push({ label: formatTimeInZone(t, tz), positionPercent: ((t - startMs) / span) * 100 });
    }
    // Always anchor the final tick to the exact shift end.
    ticks.push({ label: formatTimeInZone(endMs, tz), positionPercent: 100 });

    const rawFraction = (nowMs - startMs) / span;
    const isNowInRange = rawFraction >= 0 && rawFraction <= 1;
    const progressPercent = Math.max(0, Math.min(1, rawFraction)) * 100;

    return {
        ticks,
        progressPercent,
        nowPositionPercent: isNowInRange ? progressPercent : null,
        nowLabel: isNowInRange ? formatTimeInZone(nowMs, tz) : null,
    };
};

/**
 * Formats an absolute instant (epoch ms, or an ISO/epoch string) as 'YYYY-MM-DD' in the given
 * IANA timezone. Used to derive the site's local calendar day for the report date default. Built
 * from formatToParts so it is locale-independent (never MM/DD/YYYY).
 *
 * Degrades gracefully: since `timezoneId`/`instant` come straight from a server response, an
 * invalid IANA zone or an unparseable instant would otherwise throw a RangeError during
 * render/effect. On any such failure this falls back to the viewer's local day.
 */
export const formatDateInZone = (instant: number | string, timezoneId?: string): string => {
    const partsIn = (tz: string | undefined, date: Date) =>
        new Intl.DateTimeFormat('en-CA', {
            timeZone: tz || undefined,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        }).formatToParts(date);
    let parts: Intl.DateTimeFormatPart[];
    try {
        parts = partsIn(timezoneId, new Date(instant));
    } catch {
        // Invalid IANA zone or unparseable instant — use the viewer's local day.
        parts = partsIn(undefined, new Date());
    }
    /* istanbul ignore next -- the formatter always emits year/month/day; the ?? '' is defensive */
    const get = (type: string) => parts.find(p => p.type === type)?.value ?? '';
    return `${get('year')}-${get('month')}-${get('day')}`;
};

/**
 * True when `nowMs` falls within the shift window [shiftStartTime, shiftEndTime) — i.e. the shift
 * is currently in progress. Callers pass the skew-corrected device clock (see ShiftTimeline).
 * Returns false when the window is missing or invalid, so a closed/past shift shows no marker.
 */
export const isShiftInProgress = (
    context: ShiftTimeContext | undefined,
    nowMs: number
): boolean => {
    if (!context?.shiftStartTime || !context?.shiftEndTime) return false;
    const startMs = new Date(context.shiftStartTime).getTime();
    const endMs = new Date(context.shiftEndTime).getTime();
    if (Number.isNaN(startMs) || Number.isNaN(endMs) || endMs <= startMs) return false;
    return nowMs >= startMs && nowMs < endMs;
};

// ─── Intra-Shift Trend Chart ───────────────────────────────────────

/** Which metric the intra-shift trend chart is plotting. */
export type IntraShiftTrendMetric = 'VOLUME' | 'HEADCOUNT' | 'RATE';

/**
 * One point on the intra-shift trend chart. `actual` is populated only for hours
 * with real data (solid line); `projected` is the flat-pace continuation (dashed,
 * anchored to the last actual point); `plan` is the expected value per hour
 * (dashed throughout, flat-pace-extended once plan data runs out).
 */
export interface IntraShiftTrendRow {
    hour: string;
    actual: number | null;
    projected: number | null;
    plan: number | null;
}

/**
 * The chart plots DIRECT process paths only, using the authoritative
 * `pathType` classification. INDIRECT (and — during the service rollout —
 * untagged) paths are excluded from every metric.
 */
const isDirectPath = (e: MonitoringEntityIdentityComparisonData): boolean =>
    e.monitoringEntityIdentity.processPath?.pathType === ProcessPathType.DIRECT;

/**
 * Sums a single metric's actual/expected values across the given entities.
 * `seen` is true iff at least one entity reported the metric.
 */
const sumMetric = (
    entities: MonitoringEntityIdentityComparisonData[],
    metricName: MetricName
): { actual: number; expected: number; seen: boolean } => {
    let actual = 0;
    let expected = 0;
    let seen = false;
    entities.forEach((e) => {
        const m = e.metricsComparisonList.find((x) => x.metricName === metricName);
        if (m) {
            actual += m.actualValue;
            expected += m.expectedValue;
            seen = true;
        }
    });
    return { actual, expected, seen };
};

/**
 * Blended throughput across the given entities: total volume ÷ total labor
 * hours (Σ VOLUME_PROCESSED / Σ hours), so larger paths weight the rate.
 *
 * The flow-monitoring API exposes UPH and VOLUME_PROCESSED per path but NOT
 * hours (see RnRRCCFacadeServiceModel `MetricName` — the only PROCESS_PATH
 * metrics are UPH, VOLUME_PROCESSED, HEADCOUNT). We therefore derive each path's
 * hours from its own `volume ÷ UPH`, which reconstructs Σ volume / Σ hours
 * exactly. `pick` selects the actual or expected side of each metric.
 *
 * Returns null when no path contributes a usable (UPH > 0) reading, so the row
 * renders empty rather than dividing by zero.
 */
const blendedRate = (
    entities: MonitoringEntityIdentityComparisonData[],
    pick: (m: MetricComparisonData) => number
): number | null => {
    let volumeSum = 0;
    let hoursSum = 0;
    let seen = false;
    entities.forEach((e) => {
        const vol = e.metricsComparisonList.find((x) => x.metricName === MetricName.VOLUME_PROCESSED);
        const uph = e.metricsComparisonList.find((x) => x.metricName === MetricName.UPH);
        if (!vol || !uph) return;
        const rate = pick(uph);
        if (rate > 0) {
            volumeSum += pick(vol);
            hoursSum += pick(vol) / rate;
            seen = true;
        }
    });
    return seen && hoursSum > 0 ? Math.round(volumeSum / hoursSum) : null;
};

const parseHourLabel = (label: string): number => Number(label.split(':')[0]);
const formatHourLabel = (hour: number): string => `${String(hour).padStart(2, '0')}:00`;

/**
 * `count` hour labels strictly after `afterLabel`, stepping forward hourly and
 * wrapping past midnight (e.g. "22:00" → ["23:00", "00:00", "01:00"]).
 */
const projectionHourLabels = (afterLabel: string, count: number): string[] => {
    const labels: string[] = [];
    let h = parseHourLabel(afterLabel);
    for (let i = 0; i < count; i++) {
        h = (h + 1) % 24;
        labels.push(formatHourLabel(h));
    }
    return labels;
};

const MINUTES_PER_DAY = 24 * 60;

/** "HH:mm" → minutes after midnight (24:00 → 0); null if not a clock time. */
const toClockMinutes = (label: string): number | null => {
    const match = /^(\d{1,2}):(\d{2})$/.exec(label.trim());
    return match ? ((Number(match[1]) % 24) * 60 + Number(match[2])) : null;
};

/**
 * Absolute start of a window labelled with a site-local "HH:mm", placed at its
 * offset from the shift start (so labels past midnight land on the next day).
 * Falls back to the shift start when the label can't be parsed or falls outside
 * the shift.
 */
const windowStartMs = (label: string, shiftStartMs: number, shiftEndMs: number, timezoneId?: string): number => {
    const windowMinutes = toClockMinutes(label);
    const shiftStartMinutes = toClockMinutes(formatTimeInZone(shiftStartMs, timezoneId));
    if (windowMinutes == null || shiftStartMinutes == null) return shiftStartMs;
    const offsetMs = (((windowMinutes - shiftStartMinutes) + MINUTES_PER_DAY) % MINUTES_PER_DAY) * 60 * 1000;
    const result = shiftStartMs + offsetMs;
    return result < shiftEndMs ? result : shiftStartMs;
};

const roundedAverage = (values: number[]): number | null =>
    values.length ? Math.round(values.reduce((sum, v) => sum + v, 0) / values.length) : null;

/**
 * Consolidates the hourly comparison data into chart rows for the given metric,
 * across DIRECT process paths only (INDIRECT / untagged paths are excluded — see
 * {@link isDirectPath}). When no DIRECT path is present for an hour the row is
 * empty, so the chart renders nothing rather than misattributed data.
 *
 * - VOLUME: sums VOLUME_PROCESSED across the DIRECT paths.
 * - HEADCOUNT: sums HEADCOUNT across the DIRECT paths.
 * - RATE: the blended DIRECT throughput — total volume ÷ total labor hours,
 *   with per-path hours derived from volume ÷ UPH (see {@link blendedRate}).
 *
 * Hours present in the data get a solid `actual` and (dashed) `plan`. From the
 * end of the available data to `shiftEndTime`, a flat-pace projection (the
 * running average of the elapsed hours) fills `projected` (dashed, anchored to
 * the last actual point) and extends `plan`. Returns [] when there is no data.
 *
 * VOLUME is plotted **cumulatively**: each hour shows the running total of all
 * DIRECT volume up to and including that hour (so the curve only ever rises).
 * The flat-pace projection then keeps accumulating — each projected hour adds
 * the average hourly volume on top of the last cumulative value. HEADCOUNT and
 * RATE stay on an hour-to-hour basis (a staffing level / throughput per hour
 * does not sum meaningfully across the shift).
 */
export const buildIntraShiftTrend = (
    hourly: HourlyShiftComparisonData[] | undefined,
    shiftTimeContext: ShiftTimeContext | undefined,
    metric: IntraShiftTrendMetric
): IntraShiftTrendRow[] => {
    if (!hourly || hourly.length === 0) return [];

    // Cumulative only makes sense for a flow quantity (volume); a headcount or a
    // rate is a per-hour reading, not something to sum across the shift.
    const cumulative = metric === 'VOLUME';

    // Per-hour actual/plan for the elapsed (data) hours, before any accumulation.
    const perHour = hourly.map((window) => {
        const entities = (window.monitoringEntityIdentitiesComparisonDataList ?? []).filter(isDirectPath);
        let actual: number | null = null;
        let plan: number | null = null;

        if (metric === 'RATE') {
            // Blended DIRECT throughput = Σ volume / Σ hours, hours derived per
            // path from volume ÷ UPH (the API has no HOURS metric).
            actual = blendedRate(entities, (m) => m.actualValue);
            plan = blendedRate(entities, (m) => m.expectedValue);
        } else {
            const metricName = metric === 'VOLUME' ? MetricName.VOLUME_PROCESSED : MetricName.HEADCOUNT;
            const { actual: actualSum, expected: planSum, seen } = sumMetric(entities, metricName);
            if (seen) {
                actual = Math.round(actualSum);
                plan = Math.round(planSum);
            }
        }

        return { hour: window.startTime, actual, plan };
    });

    // Flat pace = average per-hour value over the hours that actually have data
    // (computed on the raw per-hour values, so it stays a valid hourly increment
    // even when the series is being accumulated).
    const actualRate = roundedAverage(perHour.map((r) => r.actual).filter((v): v is number => v != null));
    const planRate = roundedAverage(perHour.map((r) => r.plan).filter((v): v is number => v != null));

    // Build the data rows. For cumulative VOLUME each value is the running total
    // up to that hour; otherwise the value passes through unchanged.
    let cumActual = 0;
    let cumPlan = 0;
    const rows: IntraShiftTrendRow[] = perHour.map((r) => {
        let actual = r.actual;
        let plan = r.plan;
        if (cumulative) {
            if (actual != null) {
                cumActual += actual;
                actual = cumActual;
            }
            if (plan != null) {
                cumPlan += plan;
                plan = cumPlan;
            }
        }
        return { hour: r.hour, actual, plan, projected: null };
    });

    const shiftStartMs = shiftTimeContext?.shiftStartTime != null ? new Date(shiftTimeContext.shiftStartTime).getTime() : NaN;
    const endMs = shiftTimeContext?.shiftEndTime != null ? new Date(shiftTimeContext.shiftEndTime).getTime() : NaN;
    if (Number.isNaN(shiftStartMs) || Number.isNaN(endMs) || endMs <= shiftStartMs || actualRate == null) return rows;

    // The rows cover the shift from the first returned window, not necessarily from
    // shift start: a mid-shift re-plan only returns the windows from its cut-over on.
    // Count slots from max(shift start, first window start) so those missing early
    // hours aren't mistaken for hours still to project.
    const startMs = Math.max(
        shiftStartMs,
        windowStartMs(hourly[0].startTime, shiftStartMs, endMs, shiftTimeContext?.timezoneId)
    );

    // Total hourly slots in the shift; the data fills the first rows.length of
    // them, and the rest are projected forward to the shift end.
    const totalSlots = Math.round((endMs - startMs) / HOUR_MS);
    const projectedCount = Math.max(0, totalSlots - rows.length);
    if (projectedCount === 0) return rows;

    const labels = projectionHourLabels(rows[rows.length - 1].hour, projectedCount);

    // Anchor the projected (dashed) line to the last actual point so it connects.
    const last = rows[rows.length - 1];
    rows[rows.length - 1] = { ...last, projected: last.actual };

    // For cumulative VOLUME the projection keeps building on the last totals,
    // adding the flat hourly pace each hour; otherwise it plots the flat pace.
    let projActual = cumulative ? (last.actual ?? cumActual) : 0;
    labels.forEach((hour) => {
        let projected: number;
        let plan: number | null;
        if (cumulative) {
            projActual += actualRate;
            cumPlan += planRate ?? 0;
            projected = projActual;
            plan = cumPlan;
        } else {
            projected = actualRate;
            plan = planRate;
        }
        rows.push({ hour, actual: null, projected, plan });
    });

    return rows;
};
